/**
 * The keep-alive clock shared with the legacy ASP app.
 *
 * Both stacks ping `/Login/persist.asp` to stop `Flux..tblSession` expiring at its 20 minute idle
 * window, and both are served from the same host, so they share one localStorage origin and can
 * share one timestamp instead of each running a private timer.
 *
 * The value means "the session was last known to be RENEWED" — not "we last pinged" — because a
 * full document load renews it on its own. A legacy page runs `proc_GetSession` through
 * `/inc/const.asp`; a React boot calls `Auth/authorize`, which reaches the same row via
 * `GetFromSessionAsync` -> `proc_APIGetSession`. Either way `dtLastAccessed` moves forward, so
 * the ping is only needed when nothing has touched the session for a whole interval — and
 * whichever tab notices first covers every other tab on the origin.
 *
 * ★ The key is duplicated in two places in the legacy repo: `hd.persistKey` in `js/main.js`, and
 * the inline stamp in `Login/persist.asp` (which the keep-alive iframe runs when it reloads
 * itself). Change it in all three or the two stacks stop seeing each other.
 */
export const KEEPALIVE_KEY = 'hd-persist';

/**
 * Legacy re-pings every 5 minutes against the same key. Differing intervals are fine: the shorter
 * one simply wins while both stacks are open, and either is comfortably inside the 20 minute
 * server-side idle window.
 */
export const KEEPALIVE_INTERVAL_MS = 600000;

/** Backoff after a failed ping — short enough for several attempts inside the idle window. */
export const KEEPALIVE_RETRY_MS = 60000;

/**
 * Last known renewal, or `null` for "unknown": nothing stored yet, junk, a clock that moved
 * backwards, or storage unavailable (private browsing, blocked cookies).
 */
export function readKeepAlive(): number | null {
    try {
        const value = Number(window.localStorage.getItem(KEEPALIVE_KEY));
        return value > 0 && value <= Date.now() ? value : null;
    } catch {
        return null;
    }
}

/** Records a renewal that just happened. Silent when storage is unavailable. */
export function stampKeepAlive(): void {
    try {
        window.localStorage.setItem(KEEPALIVE_KEY, String(Date.now()));
    } catch {
        // No storage: the interval timer alone still keeps the session alive, exactly as it did
        // before the two stacks shared a clock.
    }
}

/**
 * Whether the session has gone a whole interval without a known renewal.
 *
 * "Unknown" counts as due — we cannot prove the session is fresh, and pinging unnecessarily is
 * cheap (`proc_GetSession` rate limits the write server side) while skipping a needed ping logs
 * the user out.
 */
export function keepAliveDue(): boolean {
    const last = readKeepAlive();
    return last === null || last + KEEPALIVE_INTERVAL_MS <= Date.now();
}

/**
 * Milliseconds until the next ping is due; 0 when it already is.
 *
 * "Unknown" is treated as "renewed now" here rather than as due, because the only way to reach
 * this without a stamp is a page that has just loaded — which is itself a renewal. That keeps a
 * browser with no localStorage on exactly the old load-relative schedule.
 */
export function keepAliveDelay(): number {
    const last = readKeepAlive() ?? Date.now();
    return Math.max(last + KEEPALIVE_INTERVAL_MS - Date.now(), 0);
}
